/* eslint-disable import/order */
import { createError, useRoute, useRouteParams } from '#imports'

import { useExperimentValue } from '@backmarket/nuxt-module-experiments/useExperimentValue'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'
import { HOME } from '~/scopes/home/route-names'
import { ROUTES as PLP_ROUTES } from '~/scopes/product-list/route-names'

import { CMS } from '../routes-names'
import {
  CMS_PAGE_TYPE_BY_ROUTE_NAME,
  PAGE_NAME_BY_PLP_UUID,
} from '../service/content-service.config'
import { dewebviewifyRouteName } from '~/scopes/webviews/utils'
import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'

function getPageNameByRouteName({
  routeName,
  routeParams,
}: {
  routeName: string
  routeParams: ReturnType<typeof useRouteParams>
}) {
  const { pageName, locale } = routeParams

  switch (routeName) {
    case CMS.ABOUT_US:
      return 'about-us'
    case CMS.REFER_FRIEND:
      return 'refer-friend'
    case CMS.REPAIR_AND_CARE:
      return 'repair-and-care'
    case CMS.STATEMENT:
      return 'accessibility-statement'
    case CMS.MILITARY_DISCOUNT:
      return 'military-discount'
    case CMS.STUDENT_DISCOUNT:
      return 'student-discount'
    case CMS.END_FAST_TECH:
      return 'end-fast-tech'
    case CMS.ARTICLE_ROOT_HUB:
      return 'content-hub'
    case CMS.PROMISE:
      return 'back-market-promise'
    case CMS.GIFTS:
      return 'gifts'
    case CMS.GIFT_FINDER_HOME:
      return 'home'
    case CMS.EXPRESS_REPLACEMENT:
      return 'express-replacement'
    case CMS.CUSTOMER_CARE:
      return 'customer-care'
    case CMS.TRANSLATIONS:
      return 'translations'

    case HOME: {
      return useExperimentValue('experiment.homeCMSPageName', {
        defaultValue: 'home',
      })
    }
    case CMS.EVENT: {
      if (pageName === 'recycling') {
        return useExperimentValue('experiment.recyclingEventPageName', {
          defaultValue: pageName,
        })
      }
      if (pageName === 'good-deals') {
        return useExperimentValue('experiment.goodDealsEventPageName', {
          defaultValue: pageName,
        })
      }
      if (pageName === 'holidays') {
        return useExperimentValue('experiment.holidaysEventPageName', {
          defaultValue: pageName,
        })
      }

      return pageName
    }

    case CMS.BUYBACK: {
      if (pageName === 'home') {
        return useExperimentValue('experiment.homeBuybackPageName', {
          defaultValue: pageName,
        })
      }

      return pageName
    }

    case PLP_ROUTES.UNIVERSE: {
      const { market } = useMarketplace()

      const uuid = Array.isArray(routeParams.uuid)
        ? routeParams.uuid.join('')
        : routeParams.uuid
      const pageNames = PAGE_NAME_BY_PLP_UUID[locale as Bcp47Locale]

      if (!pageNames) {
        throw createError({
          statusCode: 404,
          statusMessage: `Universe page is not available for locale ${locale} and market ${market.countryCode}`,
          fatal: true,
        })
      }

      const universePage = pageNames[uuid]
      if (!universePage) {
        throw createError({
          statusCode: 404,
          statusMessage: `Unknown universe page UUID: ${uuid}`,
          fatal: true,
        })
      }

      const variableUniversePageNames = [
        'smartphones',
        'laptops',
        'tablets',
        'audio',
        'gaming',
        'appliances',
      ]

      if (variableUniversePageNames.includes(universePage)) {
        return useExperimentValue(
          `experiment.${universePage}UniversePageName`,
          { defaultValue: universePage },
        )
      }

      return universePage
    }

    case PRODUCT_ROUTES.MASTER_PRODUCT_PAGE: {
      // Contentful does not allow numbers in page names, so we map numeric
      // segments to their word equivalent (e.g. iphone-18-pro -> iphone-eighteen-pro).
      return routeParams.slug.replace(/\b18\b/, 'eighteen')
    }
    default:
      return pageName
  }
}

export function usePageParamsFromRoute() {
  const route = useRoute()
  const routeName = String(route.name)

  const formattedRouteName = dewebviewifyRouteName(routeName)

  const routeParams = useRouteParams()

  const { pageCategory = '', locale } = routeParams

  const pageType = CMS_PAGE_TYPE_BY_ROUTE_NAME[formattedRouteName]

  const pageName = getPageNameByRouteName({
    routeName: formattedRouteName,
    routeParams,
  })

  return {
    pageType,
    pageName,
    pageCategory,
    locale,
  }
}
